import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Heading,
  Flex,
  Text,
  useTheme,
  Spinner,
  Button,
  Input,
} from "@chakra-ui/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { AiOutlineFilter, AiOutlineClear } from "react-icons/ai";
import { Icon } from "@chakra-ui/react";
import Api from "../../services/api";

const fmtBRL = (val) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(val || 0);

// ─── Custom Tooltip ──────────────────────────────────────────────────────────

const CategoryTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <Box
      bg="gray.900"
      border="1px solid"
      borderColor="secondary"
      borderRadius="md"
      p={3}
      boxShadow="lg"
      minW="200px"
    >
      <Text color="white" fontWeight="bold" mb={2}>
        {label}
      </Text>
      {(data.porCategoria || []).map((c) => {
        if (c.valor > 0) {
           return (
             <Text key={c.nome} fontSize="sm" color={c.cor} mb={1}>
               {c.nome}: {fmtBRL(c.valor)} ({Math.round(c.percentual)}%)
             </Text>
           );
        }
        return null;
      })}
      <Text color="white" fontSize="sm" fontWeight="bold" mt={2} borderTop="1px solid" borderColor="gray.700" pt={2}>
        Total: {fmtBRL(data.total || 0)}
      </Text>
    </Box>
  );
};

// Formatter to show percentages on Y axis (stackOffset="expand" gives us 0 to 1)
const toPercent = (decimal) => `${Math.round(decimal * 100)}%`;

// ─── Main Component ──────────────────────────────────────────────────────────

const DespesasPorCategoriaChart = () => {
  const theme = useTheme();

  const [meses, setMeses] = useState(12);
  const [chartStartDate, setChartStartDate] = useState("");
  const [chartEndDate, setChartEndDate] = useState("");

  const [chartData, setChartData] = useState(null); // { categorias, meses }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchChartData = useCallback(async (numMeses, sDate = "", eDate = "") => {
    setLoading(true);
    setError(null);
    try {
      const params = { 
        meses: numMeses,
        startDate: sDate,
        endDate: eDate
      };
      const res = await Api.get("/api/financeiro/despesas-categoria", { params });
      
      setChartData(res.data);
    } catch (err) {
      console.error("Erro ao carregar despesas por categoria:", err);
      setError("Não foi possível carregar os dados.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchChartData(meses);
  }, [fetchChartData, meses]);

  const handleGenerate = () => {
    fetchChartData(meses, chartStartDate, chartEndDate);
  };

  const handleClear = () => {
    setMeses(12);
    setChartStartDate("");
    setChartEndDate("");
    fetchChartData(12, "", "");
  };

  // Build chart-ready data, showing all categories returned by backend
  const chartRows = useMemo(() => {
    if (!chartData || !chartData.meses) return [];
    
    return chartData.meses.map((m) => {
      // Just use the backend totals, no client side filtering
      const row = { 
        name: m.label, 
        total: m.total, 
        porCategoria: m.porCategoria 
      };
      
      m.porCategoria.forEach((c) => {
        row[`cat_${c.nome}`] = c.valor;
      });
      return row;
    });
  }, [chartData]);
  const categoriasToRender = useMemo(() => {
    if (!chartData || !chartData.categorias || !chartData.meses) return [];
    
    // Calculate total value per category across all months currently in view
    const totalsCat = {};
    chartData.meses.forEach(m => {
      m.porCategoria.forEach(c => {
        totalsCat[c.nome] = (totalsCat[c.nome] || 0) + c.valor;
      });
    });

    // Sort categories by total value descending (largest at bottom of the stack)
    return [...chartData.categorias].sort((a, b) => {
      const totalA = totalsCat[a.nome] || 0;
      const totalB = totalsCat[b.nome] || 0;
      return totalB - totalA; 
    });
  }, [chartData]);  return (
    <Box
      bg="surface"
      p={6}
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.800"
      position="relative"
    >
      <Heading as="h3" size="sm" color="white" mb={4}>
        Distribuição Mensal de Despesas por Categoria (%)
      </Heading>

      {/* ── Filters ── */}
      <Flex direction={{ base: "column", md: "row" }} gap={4} mb={6} align={{ md: "flex-end" }} wrap="wrap">
        {/* Empty Box acting as flex filler (Replacing the tags column to keep dates/buttons on the right) */}
        <Box flex="1" />

        {/* Date Filters */}
        <Flex gap={3} direction="row" align="flex-end">
          <Box>
            <Text fontSize="xs" color="gray.400" mb={1} fontWeight="semibold">Data Inicial</Text>
            <Input
              type="date"
              size="sm"
              bg="gray.700"
              color="white"
              value={chartStartDate}
              onChange={(e) => setChartStartDate(e.target.value)}
              borderRadius="md"
              borderColor="gray.600"
              _hover={{ borderColor: "gray.500" }}
              _focus={{ borderColor: "green.500", boxShadow: "0 0 0 1px var(--chakra-colors-green-500)" }}
              minW="130px"
              sx={{ "::-webkit-calendar-picker-indicator": { filter: "invert(1)" } }}
            />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.400" mb={1} fontWeight="semibold">Data Final</Text>
            <Input
              type="date"
              size="sm"
              bg="gray.700"
              color="white"
              value={chartEndDate}
              onChange={(e) => setChartEndDate(e.target.value)}
              borderRadius="md"
              borderColor="gray.600"
              _hover={{ borderColor: "gray.500" }}
              _focus={{ borderColor: "green.500", boxShadow: "0 0 0 1px var(--chakra-colors-green-500)" }}
              minW="130px"
              sx={{ "::-webkit-calendar-picker-indicator": { filter: "invert(1)" } }}
            />
          </Box>
        </Flex>

        {/* Actions */}
        <Flex direction="column" gap={2}>
          <Button
            colorScheme="green"
            size="sm"
            onClick={handleGenerate}
            isLoading={loading}
            loadingText="Carregando..."
            minW="120px"
            leftIcon={<Icon as={AiOutlineFilter} />}
          >
            Filtrar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClear}
            isDisabled={loading || (!chartStartDate && !chartEndDate)}
            minW="120px"
            leftIcon={<Icon as={AiOutlineClear} />}
            borderColor="gray.600"
            color="gray.400"
            _hover={{ bg: "gray.700", color: "white" }}
          >
            Limpar
          </Button>
        </Flex>
      </Flex>

      {/* States */}
      {loading && (
        <Flex justify="center" align="center" h="350px">
          <Spinner color="neon.blue" />
        </Flex>
      )}

      {error && !loading && (
        <Flex justify="center" align="center" h="350px">
          <Text color="red.400">{error}</Text>
        </Flex>
      )}

      {/* Chart */}
      {!loading && !error && chartRows.length > 0 && (
        <ResponsiveContainer width="100%" height={500}>
          <BarChart
            data={chartRows}
            margin={{ top: 20, right: 30, left: 10, bottom: 5 }}
            stackOffset={categoriasToRender.length === 0 ? "none" : "expand"} /* Fallback to none if empty */
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" vertical={false} />
            <XAxis 
              dataKey="name" 
              stroke={theme.colors.gray[700]} 
              tick={{ fill: theme.colors.gray[400], fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              dy={10}
            />
            <YAxis 
              tickFormatter={toPercent} 
              stroke={theme.colors.gray[700]} 
              tick={{ fill: theme.colors.gray[400], fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              dx={-10}
              domain={categoriasToRender.length === 0 ? [0, 1] : [0, 'auto']} /* Fix domain for empty state */
            />
            
            {categoriasToRender.length > 0 && (
              <Tooltip content={<CategoryTooltip />} cursor={{ fill: "rgba(255, 255, 255, 0.05)" }} />
            )}
            
            {categoriasToRender.length > 0 && (
              <Legend 
                wrapperStyle={{ paddingTop: "20px", fontSize: "12px", color: theme.colors.gray[400] }} 
                iconType="circle"
              />
            )}

            {/* Dummy bar to force Recharts to draw the grid and XAxis when there is no data */}
            {categoriasToRender.length === 0 && (
              <Bar dataKey="dummy" fill="transparent" />
            )}

            {categoriasToRender.length > 0 && categoriasToRender.map((cat, index) => (
              <Bar 
                key={cat.nome} 
                dataKey={`cat_${cat.nome}`} 
                name={cat.nome}
                stackId="a" 
                fill={cat.cor}
                radius={
                  index === categoriasToRender.length - 1
                    ? [4, 4, 0, 0] // Arredonda o topo apenas para a última categoria (do topo da pilha)
                    : [0, 0, 0, 0]
                }
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export default React.memo(DespesasPorCategoriaChart);
